import ReactGA4 from 'react-ga4';

import ReactPixel from 'react-facebook-pixel';
import get from 'lodash/get';

import getBasePath from 'bento-ordering/utils/getBasePath';
import { getLocationPixelSelector } from 'bento-ordering/locations/selectors';

import {
  INITIALIZE_LOCATION_PIXEL,
  SET_REFERRAL_SOURCE,
  SET_UTM_CAMPAIGN,
  FACEBOOK_ADD_TO_CART_EVENT,
  PIXEL_INIT_OPTIONS,
  GOOGLE_ANALYTICS_4
} from 'bento-ordering/base/constants/tracking';
import { validateReferralSource } from 'bento-ordering/base/utils/validateReferralSource';
import { validateUTMCampaign } from 'bento-ordering/base/utils/validateUTMCampaign';

export const initializeTracking = () => (dispatch, getState) => {
  dispatch(initializeGA4());
};

export const initializeGA4 = () => (dispatch, getState) => {
  const state = getState();
  const account = state.account;
  const googleAnalytics4Code = get(
    account,
    'google_analytics_browser.measurement_id',
    ''
  );

  if (googleAnalytics4Code) {
    ReactGA4.initialize(googleAnalytics4Code);
    dispatch({ type: GOOGLE_ANALYTICS_4 });
  }
};

export const trackPageview = (
  pageTitle = '',
  bentoAnalyticsPageViewShouldRun = true,
  extraParams = {}
) => (dispatch, getState) => {
  const state = getState();
  const router = state.router;
  const { ga4Initialized, initializedPixels, referralSource } = state.tracking;
  const path = router.location.pathname;
  const locationPixel = getLocationPixelSelector(
    state.restaurantLocations,
    state.cart.online_ordering_location
  );

  // trackPageview takes params "url, "title" and "extraParams" (not named arguments)
  // BentoAnalytics will ignore arguments if they are blank strings
  // https://github.com/getbento/analytics
  // Should run check is used because analytics always runs trackPageview on load
  if (window.BentoAnalytics && bentoAnalyticsPageViewShouldRun) {
    window.BentoAnalytics.trackPageview('', pageTitle, extraParams);
  }

  if (ga4Initialized) {
    trackGA4PageView(path);
  }

  if (
    locationPixel &&
    !initializedPixels.includes(locationPixel) &&
    referralSource
  ) {
    dispatch(trackPixelPageView(locationPixel));
  }
};

export const trackPixelPageView = locationPixel => dispatch => {
  trackSinglePixelPageView(locationPixel);
  dispatch({
    type: INITIALIZE_LOCATION_PIXEL,
    payload: { locationPixel }
  });
};

export const trackAddToCart = quantity => (dispatch, getState) => {
  const state = getState();
  const { facebook_pixel_id } = state.account;

  if (window.BentoAnalytics) {
    window.BentoAnalytics.trackEvent('Add to Cart', 'Click', 'eCom', quantity);
  }

  const locationPixel = getLocationPixelSelector(
    state.restaurantLocations,
    state.cart.online_ordering_location
  );

  const pixelParams = {
    eventCategory: 'Add To Cart',
    eventAction: 'Click',
    eventLabel: 'eCom',
    eventValue: quantity
  };

  if (facebook_pixel_id) {
    ReactPixel.init(facebook_pixel_id, {}, PIXEL_INIT_OPTIONS);
    ReactPixel.trackSingle(
      facebook_pixel_id,
      FACEBOOK_ADD_TO_CART_EVENT,
      pixelParams
    );
  }

  // also send event to location-specific pixel if there is one
  if (locationPixel) {
    ReactPixel.init(locationPixel, {}, PIXEL_INIT_OPTIONS);
    ReactPixel.trackSingle(
      locationPixel,
      FACEBOOK_ADD_TO_CART_EVENT,
      pixelParams
    );
  }
};

export const trackAddMenuUpsellItem = isUpsellHeaderABEnabled => {
  if (window.BentoAnalytics) {
    window.BentoAnalytics.trackEvent(
      'Add to Cart',
      'Click',
      'OO Upsell AB Header On',
      isUpsellHeaderABEnabled
    );
  }
};

export const trackNavigateToCartFromSnackbar = () => {
  if (window.BentoAnalytics) {
    window.BentoAnalytics.trackEvent(
      'Online Ordering',
      'Click',
      'Cart Snackbar'
    );
  }
};

export const trackNavigateToCartFromNavbar = () => {
  if (window.BentoAnalytics) {
    window.BentoAnalytics.trackEvent('Online Ordering', 'Click', 'Cart Navbar');
  }
};

export const trackGA4PageView = path => {
  // page view path needs to include base path
  path = `${getBasePath()}${path}`;
  ReactGA4.send({ hitType: 'pageview', page: path });
};

export const trackSinglePixelPageView = pixelId => {
  ReactPixel.init(pixelId, {}, PIXEL_INIT_OPTIONS);
  ReactPixel.trackSingle(pixelId, 'PageView');
};

export const setReferralSource = () => (dispatch, getState) => {
  const state = getState();
  const router = state.router;
  const { utm_source: utmSource } = router.location.query;
  const validReferralSource = validateReferralSource(utmSource);

  // Sets utm source to be valid referral source if applicable which determines if the location pixel should be fired
  if (validReferralSource) {
    dispatch({
      type: SET_REFERRAL_SOURCE,
      payload: { referralSource: validReferralSource }
    });
  }
};

export const setUTMCampaign = () => (dispatch, getState) => {
  const state = getState();
  const router = state.router;
  const { utm_campaign: utmCampaign } = router.location.query;
  const validUTMCampaign = validateUTMCampaign(utmCampaign);

  if (validUTMCampaign) {
    dispatch({
      type: SET_UTM_CAMPAIGN,
      payload: { utmCampaign: validUTMCampaign }
    });
  }
};
